import type { Dayjs, OpUnitType } from 'dayjs';
import type { ActivityDTO } from 'src/types/dto/activity.dto';

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';



dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(isSameOrBefore);



export type DatePickerFormat = Dayjs | Date | string | number | null | undefined;

/**
 * Docs: https:
 */
export const formatStr = {
  dateTime: 'DD MMM YYYY h:mm a', 
  date: 'DD MMM YYYY', 
  time: 'h:mm a', 
  split: {
    dateTime: 'DD/MM/YYYY h:mm a', 
    date: 'DD/MM/YYYY', 
  },
  paramCase: {
    dateTime: 'DD-MM-YYYY h:mm a', 
    date: 'DD-MM-YYYY', 
  },
};

export function today(format?: string) {
  return dayjs(new Date()).startOf('day').format(format);
}



/** output: 17 Apr 2022 12:00 am
 */
export function fDateTime(date: DatePickerFormat, format?: string) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid ? dayjs(date).format(format ?? formatStr.dateTime) : 'Invalid time value';
}



/** output: 17 Apr 2022
 */
export function fDate(date: DatePickerFormat, format?: string) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid ? dayjs(date).format(format ?? formatStr.date) : 'Invalid time value';
}

/** output: 17/04/2022
 */
export function fDateDMY(date: DatePickerFormat) {
  return fDate(date, formatStr.split.date);
}



/** output: 12:00 am
 */
export function fTime(date: DatePickerFormat, format?: string) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid ? dayjs(date).format(format ?? formatStr.time) : 'Invalid time value';
}



/** output: 1713250100
 */
export function fTimestamp(date: DatePickerFormat) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid ? dayjs(date).valueOf() : 'Invalid time value';
}



/** output: a few seconds, 2 years
 */
export function fToNow(date: DatePickerFormat) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid ? dayjs(date).toNow(true) : 'Invalid time value';
}



/** output: boolean
 */
export function fIsBetween(
  inputDate: DatePickerFormat,
  startDate: DatePickerFormat,
  endDate: DatePickerFormat
) {
  if (!inputDate || !startDate || !endDate) {
    return false;
  }

  const formattedInputDate = fTimestamp(inputDate);
  const formattedStartDate = fTimestamp(startDate);
  const formattedEndDate = fTimestamp(endDate);

  if (formattedInputDate && formattedStartDate && formattedEndDate) {
    return formattedInputDate >= formattedStartDate && formattedInputDate <= formattedEndDate;
  }

  return false;
}



/** output: boolean
 */
export function fIsAfter(startDate: DatePickerFormat, endDate: DatePickerFormat) {
  return dayjs(startDate).isAfter(endDate);
}



/** output: boolean
 */
export function fIsSame(
  startDate: DatePickerFormat,
  endDate: DatePickerFormat,
  units?: OpUnitType
) {
  if (!startDate || !endDate) {
    return false;
  }

  const isValid = dayjs(startDate).isValid() && dayjs(endDate).isValid();

  if (!isValid) {
    return 'Invalid time value';
  }

  return dayjs(startDate).isSame(endDate, units ?? 'year');
}



/** output:
 * Same day: 26 Apr 2024
 * Same month: 25 - 26 Apr 2024
 * Same month: 25 - 26 Apr 2024
 * Same year: 25 Apr - 26 May 2024
 */
export function fDateRangeShortLabel(
  startDate: DatePickerFormat,
  endDate: DatePickerFormat,
  initial?: boolean
) {
  const isValid = dayjs(startDate).isValid() && dayjs(endDate).isValid();

  const isAfter = fIsAfter(startDate, endDate);

  if (!isValid || isAfter) {
    return 'Invalid time value';
  }

  let label = `${fDate(startDate)} - ${fDate(endDate)}`;

  if (initial) {
    return label;
  }

  const isSameYear = fIsSame(startDate, endDate, 'year');
  const isSameMonth = fIsSame(startDate, endDate, 'month');
  const isSameDay = fIsSame(startDate, endDate, 'day');

  if (isSameYear && !isSameMonth) {
    label = `${fDate(startDate, 'DD MMM')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && !isSameDay) {
    label = `${fDate(startDate, 'DD')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && isSameDay) {
    label = `${fDate(endDate)}`;
  }

  return label;
}

/**
 * Formats the completion window of an activity
 * @param activity The activity to format the completion window for
 * @returns Formatted completion window string
 * 
 * output examples:
 * 26 Apr 2024 - 28 Apr 2024
 * 26 Apr 2024 - 
 */
export const getCompletionWindow = (activity: ActivityDTO) =>
  activity.startDate
    ? `${dayjs(activity.startDate).isValid() ? dayjs(activity.startDate).format('DD MMM YY') : ''} - ${dayjs(activity.dueDate).isValid() ? dayjs(activity.dueDate).format('DD MMM YY') : ''}`
    : `${dayjs().add(1, 'day').format('DD MMM YY')} - ${dayjs(activity.dueDate).isValid() ? dayjs(activity.dueDate).format('DD MMM YY') : ''}`;

/**
 * Formats a date range based on an end date and a period duration
 * @param endDate The end date of the range
 * @param period The duration to subtract from the end date (e.g., { months: 6 } or { years: 2 })
 * @param initial Whether to skip smart formatting and always show full dates
 * @returns Formatted date range string
 * 
 * output examples:
 * 26 Apr 2024
 */
export function fStartDateShortLabel(startDate: DatePickerFormat) {
  if (!startDate) {
    return 'Invalid time value';
  }

  return fDate(startDate);
}

/** 
 * Formats a date range based on an end date and a period duration
 * @param endDate The end date of the range
 * @param period The duration to subtract from the end date (e.g., { months: 6 } or { years: 2 })
 * @param initial Whether to skip smart formatting and always show full dates
 * @returns Formatted date range string
 * 
 * output examples:
 * Same day: 26 Apr 2024
 * Same month: 25 - 26 Apr 2024
 * Same year: 25 Apr - 26 May 2024
 * Different years: 25 Apr 2023 - 26 Apr 2024
 */
export function fDateRangeFromPeriod(
  endDate: DatePickerFormat,
  period: DurationProps,
  initial?: boolean
) {
  if (!endDate) {
    return 'Invalid time value';
  }

  const isValidEnd = dayjs(endDate).isValid();
  if (!isValidEnd) {
    return 'Invalid time value';
  }

  const startDate = dayjs(endDate).subtract(dayjs.duration(period));
  
  return fDateRangeShortLabel(startDate, endDate, initial);
}



export type DurationProps = {
  years?: number;
  months?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
  milliseconds?: number;
};

/** output: '2024-05-28T05:55:31+00:00'
 */
export function fAdd({
  years = 0,
  months = 0,
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0,
}: DurationProps) {
  const result = dayjs()
    .add(
      dayjs.duration({
        years,
        months,
        days,
        hours,
        minutes,
        seconds,
        milliseconds,
      })
    )
    .format();

  return result;
}

/** output: '2024-05-28T05:55:31+00:00'
 */
export function fSub({
  years = 0,
  months = 0,
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0,
}: DurationProps) {
  const result = dayjs()
    .subtract(
      dayjs.duration({
        years,
        months,
        days,
        hours,
        minutes,
        seconds,
        milliseconds,
      })
    )
    .format();

  return result;
}



/** 
 * Returns an array of months within the given date range
 * Each month is represented as a date object set to the first day of that month
 * 
 * @param startDateOrEndDate The start date of the range, or the end date if using period
 * @param endDateOrPeriod The end date of the range, or the period if using duration-based range
 * @param isPeriod Whether the second parameter is a period (defaults to false)
 * @returns Array of dates, each representing the first day of each month in the range
 * 
 * Example outputs:
 * fGetMonthsInRange('2024-01-15', '2024-03-20') 
 * => [Date('2024-01-01'), Date('2024-02-01'), Date('2024-03-01')]
 * 
 * fGetMonthsInRange('2024-03-20', { months: 2 }, true)
 * => [Date('2024-02-01'), Date('2024-03-01')]
 */
export function fGetMonthsInRange(
  startDateOrEndDate: DatePickerFormat,
  endDateOrPeriod: DatePickerFormat | DurationProps,
  isPeriod: boolean = false
): Date[] {
  let startDate: Dayjs;
  let endDate: Dayjs;

  if (isPeriod && typeof endDateOrPeriod === 'object' && !dayjs.isDayjs(endDateOrPeriod) && !(endDateOrPeriod instanceof Date)) {
    endDate = dayjs(startDateOrEndDate);
    startDate = dayjs(endDate).subtract(dayjs.duration(endDateOrPeriod as Partial<DurationProps>));
  } else {
    startDate = dayjs(startDateOrEndDate);
    endDate = dayjs(endDateOrPeriod as DatePickerFormat);
  }

  if (!startDate.isValid() || !endDate.isValid()) {
    return [];
  }

  if (fIsAfter(startDate, endDate)) {
    return [];
  }

  const months: Date[] = [];
  let currentMonth = startDate.startOf('month');
  const lastMonth = endDate.startOf('month');

  while (currentMonth.isSameOrBefore(lastMonth)) {
    months.push(currentMonth.toDate());
    currentMonth = currentMonth.add(1, 'month');
  }

  return months;
}




export function formatDate(date: Date | string, formatPattern: string = 'dd MMM yyyy'): string {
  const d = new Date(date);
  
  
  const day = d.getDate().toString().padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[d.getMonth()];
  const year = d.getFullYear();
  
  
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  
  
  if (formatPattern === 'dd MMM yyyy') {
    return `${day} ${month} ${year}`;
  } if (formatPattern === 'HH:mm') {
    return `${hours}:${minutes}`;
  } if (formatPattern === 'dd MMM yyyy, HH:mm') {
    return `${day} ${month} ${year}, ${hours}:${minutes}`;
  }
  
  
  return `${day} ${month} ${year}`;
}


// ----------------------------------------------------------------------

/** 
 * Formats seconds into hours and minutes in the format '1h 40m' or '0h 35m'
 * @param seconds The number of seconds to format
 * @returns Formatted duration string
 * 
 * Example outputs:
 * fFormatDuration(6000) => '1h 40m'
 * fFormatDuration(2100) => '0h 35m'
 */
export function fFormatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  return seconds ? `${hours}h ${minutes}min` : '0h 0m';
}

// ----------------------------------------------------------------------
